import {
  Accordion,
  CTA,
  Divider,
  styled,
  Text,
  TextTag,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import { ReactElement, useContext, useMemo, useState } from 'react';
import { EquipmentOption } from '@org-avp/avp-hck-dga-customerui-commons';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from '@volkswagen-onehub/icons-core';
import { GiveawayContext } from '@/state/state';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { AccordionState, TrackingEvent } from '@/util/tracking/definitions';

export const StyledText = styled(Text)`
  padding: ${(props) => props.theme.size.static200} 4px;
`;

export const StyledHeadline = styled(Text)`
  padding-bottom: ${(props) => props.theme.size.static200};
`;

enum EQUIPMENT_TYPES {
  STANDARD = 'STANDARD_EQUIPMENT',
  OPTIONAL = 'OPTIONAL_EQUIPMENT',
}

// I and L are classified as standard equipment
const specialVinEquipment = new Set(['I', 'L']);
// E, G, P are classified as special equipment
const optionalVinEquipment = new Set(['E', 'G', 'P']);

interface AccordionItems {
  id?: string;
  description?: string;
}

interface SortedEquipment {
  category: string;
  accordionItems: AccordionItems[];
}

const StyledButton = styled(CTA)`
  margin: 40px auto auto auto;
  display: flex;
`;

export const EquipmentAccordion = (): ReactElement => {
  const giveawayResponse = useContext(GiveawayContext);
  const { equipmentOptions, marketingCode } = giveawayResponse;
  const [openAccordion, setOpenAccordion] = useState<Set<string>>(new Set());
  const { t } = useTranslation();
  const trackingService = useTrackingService();
  const isVin = marketingCode === 'VIN';
  const [showAll, setShowAll] = useState(false);

  const handleOpenAccordion = (category: string) => {
    setOpenAccordion((prevCategories) => {
      const updatedCategories = new Set(prevCategories);
      const isOpening = !updatedCategories.has(category);

      if (isOpening) {
        updatedCategories.add(category);
      } else {
        updatedCategories.delete(category);
      }

      trackEvent(isOpening, category);
      return updatedCategories;
    });
  };

  const trackEvent = (isOpening: boolean, category: string) => {
    if (isOpening) {
      return trackingService.track(TrackingEvent.VWBasicAccordionOpenClick, {
        accordion: { groupItemName: category, groupItemState: AccordionState.OPEN },
        process: { StepName: `equipment ${category}` },
        eventInfo: { contentId: `opened equipment accordion category: ${category}` },
      });
    }

    return trackingService.track(TrackingEvent.VWBasicAccordionCloseClick, {
      accordion: { groupItemName: category, groupItemState: AccordionState.CLOSED },
      process: { StepName: `equipment ${category}` },
      eventInfo: { contentId: `closed equipment accordion category: ${category}` },
    });
  };

  const sortEquipmentCategories = (
    grouped?: Record<string, EquipmentOption[]>,
  ): SortedEquipment[] => {
    return Object.keys(grouped as Record<string, EquipmentOption[]>)
      .sort()
      .map((category) => {
        const sortedItems = grouped?.[category].sort((a, b) => {
          const descA = a.description ?? '';
          const descB = b.description ?? '';
          return descA.localeCompare(descB);
        });

        return {
          category,
          accordionItems: sortedItems?.map((item) => ({
            id: item.id,
            description: item.description,
          })),
        } as SortedEquipment;
      });
  };

  const addOptionalEquipmentCategory = (
    allCategories: SortedEquipment[],
    optionalEquipmentItems: EquipmentOption[],
  ) => {
    const optionalCategory = {
      category: 'equipmentAccordion.additionalEquipment',
      accordionItems: optionalEquipmentItems?.map((item: EquipmentOption) => ({
        id: item.id,
        description: item.description,
      })),
    };

    return [...allCategories, optionalCategory];
  };

  const groupEquipmentOptionsByCategory = (equipmentData?: EquipmentOption[]) => {
    if (!equipmentData) return [];

    // Group EQUIPMENT_TYPES.STANDARD by category
    const grouped = equipmentData?.reduce(
      (acc: Record<string, EquipmentOption[]>, item: EquipmentOption) => {
        const category = item.category?.trim();
        // Handle STANDARD equipment (type only available for marketing codes)
        if (category && item.classification === EQUIPMENT_TYPES.STANDARD && !isVin) {
          if (!(category in acc)) {
            acc[category] = [];
          }
          acc[category].push(item);
        }

        // Use a different sorting for VINs
        if (
          category &&
          isVin &&
          item.classification &&
          specialVinEquipment.has(item.classification)
        ) {
          if (!(category in acc)) {
            acc[category] = [];
          }

          acc[category].push(item);
        }

        return acc;
      },
      {} as Record<string, EquipmentOption[]>,
    );

    const sortedStandardCategories = sortEquipmentCategories(grouped);

    // Check if EQUIPMENT_TYPES.OPTIONAL is available
    // If yes set it to the end of the equipment array
    const optionalEquipmentItems = equipmentData?.filter(
      (item) => item.classification === EQUIPMENT_TYPES.OPTIONAL,
    );

    // Filter all special Equipment for VINs
    const optionalVinEquipmentItems = equipmentData?.filter(
      (item) => item.classification && optionalVinEquipment.has(item.classification),
    );

    if (optionalEquipmentItems && !isVin) {
      return addOptionalEquipmentCategory(sortedStandardCategories, optionalEquipmentItems);
    }

    if (optionalVinEquipmentItems && isVin) {
      return addOptionalEquipmentCategory(sortedStandardCategories, optionalVinEquipmentItems);
    }

    return sortedStandardCategories;
  };

  const groupedAccordionOptions = useMemo(
    () => groupEquipmentOptionsByCategory(equipmentOptions),
    [equipmentOptions, isVin],
  );

  const visibleAccordions = useMemo(() => {
    return showAll ? groupedAccordionOptions : groupedAccordionOptions.slice(0, 5);
  }, [showAll, groupedAccordionOptions]);

  return (
    <div data-testid="accordion-container">
      {visibleAccordions.map(({ category, accordionItems }) => (
        <Accordion
          data-testid="accordion"
          id={category}
          key={category}
          headline={
            <Text tag={TextTag.h2} appearance={TokenTextAppearance.headline200} bold>
              {t(category, { defaultValue: category })}
            </Text>
          }
          onClick={() => handleOpenAccordion(category)}
          isOpen={openAccordion.has(category)}
        >
          {accordionItems?.map((item: EquipmentOption, index: number) => (
            <div key={item.id}>
              <StyledText tag={TextTag.p}>{item.description}</StyledText>
              {/* Render the divider only if this is not the last item */}
              {index < accordionItems.length - 1 && <Divider />}
            </div>
          ))}
        </Accordion>
      ))}
      {/*
        There might be cases where we have less than or exactly 5 accordions.
        In that case we don't need a show all button.
      */}
      {groupedAccordionOptions.length > 5 && (
        <StyledButton
          icon={
            showAll ? (
              <ChevronUp variant="small" aria-label={t(`equipmentAccordion.showLessButton`)} />
            ) : (
              <ChevronDown variant="small" aria-label={t(`equipmentAccordion.showMoreButton`)} />
            )
          }
          tag="button"
          emphasis="secondary"
          aria-label={
            showAll
              ? t('equipmentAccordion.showLessButton')
              : t('equipmentAccordion.showMoreButton')
          }
          onClick={() => {
            if (showAll) {
              setOpenAccordion(new Set());
            }
            setShowAll(!showAll);
          }}
        >
          {showAll
            ? t('equipmentAccordion.showLessButton')
            : t('equipmentAccordion.showMoreButton')}
        </StyledButton>
      )}
    </div>
  );
};
